import { useCallback, useMemo } from 'react'

import { VariableSelectFeatureFlag } from '@makyoapp/makyo-common'
import {
  ColorVariant,
  ComponentAlignment,
  HoverEffectVariant,
  HoverIntensity,
  MaterialIcon,
  RawSpacingInput,
  TypographyValue,
  VerticalMenu,
} from '@makyoapp/makyo-tw-ui'

import { useActiveLayerContext } from '@/context/__project__/ActiveLayerContext'
import { useAppAuthContext } from '@/context/__project__/AppAuthContext'
import { useBuilderActionContextV2 } from '@/context/__project__/BuilderActionContextV2'
import { useCompContext } from '@/context/__project__/CompContext'
import { useDbClientContext } from '@/context/__project__/DbClientContext'
import { useAppBuilderContext } from '@/context/AppBuilderContext'
import { checkVisibilityV2 } from '@/hooks/__project__/useCheckVisibilityV2'
import { useBreakpoint } from '@/hooks/useBreakpoint'

const useMenuItemsVisibility = (menuItems: any[]) => {
  const { dbClient, language, userSpecific } = useDbClientContext()
  const { userEmail } = useAppAuthContext()
  const { activeLayerTableId, activeLayerDetailRowIndex } = useActiveLayerContext()
  const { device } = useBreakpoint()

  const menuItemsKey = JSON.stringify(menuItems)

  return useMemo(() => {
    if (!menuItems || menuItems.length === 0) return []

    return menuItems.filter((menu) => {
      const isVisible = checkVisibilityV2(
        menu?.visibility,
        dbClient,
        userEmail,
        language,
        userSpecific,
        activeLayerTableId,
        activeLayerDetailRowIndex,
        undefined, // formState
        device
      )
      return isVisible
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [menuItemsKey, dbClient, userEmail, language, userSpecific, activeLayerTableId, activeLayerDetailRowIndex, device])
}

export interface CompVerticalMenuProps {
  menuItems: any[]
  spacing: number
  title: string
  backgroundColor: ColorVariant
  margin: RawSpacingInput

  titleTypo: TypographyValue
  itemsTypo: TypographyValue
  orientation: string
  alignment: string
  titleMargin: RawSpacingInput
  itemMargin: RawSpacingInput
  menuAlignment: string

  titleColor: 'adaptive' | 'solid' | 'gradient'
  itemsColor: 'adaptive' | 'solid' | 'gradient'
  titleColorSolid: ColorVariant
  itemsColorSolid: ColorVariant
  titleColorStartGradient: ColorVariant
  itemsColorStartGradient: ColorVariant
  titleColorEndGradient: ColorVariant
  titleColorGradientDirection: ColorVariant
  itemsColorEndGradient: ColorVariant
  itemsColorGradientDirection: ColorVariant

  showIcon: boolean
  showTitle: boolean

  hoverEffect: HoverEffectVariant
  hoverIntensity: HoverIntensity

  // Overflow menu props
  maxVisibleItems?: number
  overflowIcon?: string
  overflowMenuProps?: {
    backgroundColor?: ColorVariant
    buttonSize?: 'sm' | 'md' | 'lg' | 'icon'
    buttonShape?: string
    dropdownAlign?: 'start' | 'center' | 'end'
  }
}

export function CompVerticalMenuCore({
  alignment,
  menuItems,
  spacing,
  title,
  backgroundColor,
  titleTypo,
  itemsTypo,
  titleMargin,
  itemMargin,
  titleColor,
  itemsColor,
  titleColorSolid,
  itemsColorSolid,
  titleColorStartGradient,
  itemsColorStartGradient,
  titleColorEndGradient,
  titleColorGradientDirection,
  itemsColorEndGradient,
  itemsColorGradientDirection,
  showIcon,
  margin,
  hoverEffect,
  hoverIntensity,
  orientation,
  showTitle,
  menuAlignment,
  maxVisibleItems,
  overflowIcon,
  overflowMenuProps,
}: CompVerticalMenuProps) {
  const { simulateAction } = useBuilderActionContextV2()
  const { activePageTableId, activePageRowIndex, activeLayerDetailRowId } = useActiveLayerContext()
  const { isDevelopmentMode, openComponentSettings } = useAppBuilderContext()
  const { component } = useCompContext()

  const extractTextFromJson = useCallback((jsonStr: string) => {
    const extractTextFromNode = (node: any): string => {
      if (typeof node === 'string') return node
      if (node.text) return node.text

      if (Array.isArray(node)) {
        return node.map(extractTextFromNode).join('')
      }

      if (node.children) {
        return extractTextFromNode(node.children)
      }

      return ''
    }

    try {
      const parsed = JSON.parse(jsonStr)
      return extractTextFromNode(parsed)
    } catch {
      return jsonStr
    }
  }, [])

  const menuItemsWithVisibility = useMenuItemsVisibility(menuItems || [])

  const renderMenuItems = () => {
    return menuItemsWithVisibility.map((menu) => {
      return {
        icon: showIcon ? menu.icon?.value : undefined,
        title: typeof menu.title === 'string' ? extractTextFromJson(menu.title) : menu.title,
        actions: menu.actions,
      }
    })
  }

  const handleClick = (menu: any) => {
    openComponentSettings(component)
    if (isDevelopmentMode) return
    simulateAction({
      actions: menu?.actions || [],
      rowIndex: activePageRowIndex,
      tableId: activePageTableId,
      rowId: activeLayerDetailRowId,
    })
  }

  if (renderMenuItems().length === 0) return null

  return (
    <VerticalMenu
      menuItems={renderMenuItems()}
      spacing={spacing}
      title={title && typeof title === 'string' ? extractTextFromJson(title) : ''}
      backgroundColor={backgroundColor}
      titleTypography={titleTypo}
      itemsTypography={itemsTypo}
      titleMargin={titleMargin}
      itemMargin={itemMargin}
      handleClick={handleClick}
      titleColorType={titleColor}
      titleColorSolid={titleColorSolid}
      titleColorStartGradient={titleColorStartGradient}
      titleColorEndGradient={titleColorEndGradient}
      titleColorGradientDirection={titleColorGradientDirection}
      menuItemColorType={itemsColor}
      menuItemColorSolid={itemsColorSolid}
      menuItemColorStartGradient={itemsColorStartGradient}
      menuItemColorEndGradient={itemsColorEndGradient}
      menuItemColorGradientDirection={itemsColorGradientDirection}
      alignment={alignment as ComponentAlignment}
      margin={margin}
      devMode={isDevelopmentMode}
      hoverEffect={hoverEffect}
      hoverIntensity={hoverIntensity}
      orientation={orientation as 'horizontal' | 'vertical'}
      showTitle={showTitle}
      menuAlignment={menuAlignment as 'start' | 'center' | 'end'}
      maxVisibleItems={maxVisibleItems}
      overflowIcon={overflowIcon}
      overflowMenuProps={overflowMenuProps as any}
    />
  )
}
